import React, { useState, useEffect } from 'react';
import { Mail, Inbox, Send, Settings, RefreshCw, Plus, Filter } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../foundation/altzero_base/base/components/ui/card';
import { Button } from '../../../foundation/altzero_base/base/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../foundation/altzero_base/base/components/ui/tabs';
import { useToast } from '../../../foundation/altzero_base/base/hooks/use-toast';
import { EmailAccountManager } from '../components/email/EmailAccountManager';
import { EmailCompositionDialog } from '../components/email/EmailCompositionDialog';
import { emailApiService, EmailAccount } from '../services/email/emailApiService';
import { getTenders, Tender } from '../services/tenderService';
import ScopingAILayout from '../components/ScopingAILayout';
import { RetailerCommunicationHub } from '../components/email/RetailerCommunicationHub';
import { SentEmailList } from '../components/email/SentEmailList';
import { RetailerResponseViewer } from '../components/email/RetailerResponseViewer';
import { FileText } from 'lucide-react';
import EmailTemplateManager from '../components/email/EmailTemplateManager';

export default function EmailManagement() {
    const { toast } = useToast();
    const [activeTab, setActiveTab] = useState('inbox');
    const [accounts, setAccounts] = useState<EmailAccount[]>([]);
    const [stats, setStats] = useState({ accounts: 0, inbox: 0, sent: 0 });
    const [tenders, setTenders] = useState<Tender[]>([]);
    const [selectedTenderId, setSelectedTenderId] = useState<string>('');
    const [loading, setLoading] = useState(false);
    const [isComposingEmail, setIsComposingEmail] = useState(false);
    const [lastSyncedAt, setLastSyncedAt] = useState<number>(Date.now());

    useEffect(() => {
        loadInitialData();
    }, []);

    const loadInitialData = async () => {
        try {
            setLoading(true);
            const [accountsData, statsData, tendersResult] = await Promise.all([
                emailApiService.getAccounts(),
                emailApiService.getStats(),
                getTenders()
            ]);

            setAccounts(accountsData);
            setStats(statsData);
            if (tendersResult.success && tendersResult.data) {
                setTenders(tendersResult.data);
            }
        } catch (error: any) {
            toast({
                title: 'Error',
                description: error.message || 'Failed to load data',
                variant: 'destructive',
            });
        } finally {
            setLoading(false);
        }
    };

    const handlePollEmails = async () => {
        try {
            setLoading(true);
            await emailApiService.pollEmails();
            toast({
                title: 'Success',
                description: 'Emails synced successfully',
            });
            // Refresh stats and data
            loadInitialData();
            // Trigger refresh in child components
            setLastSyncedAt(Date.now());
        } catch (error: any) {
            toast({
                title: 'Error',
                description: error.message || 'Failed to sync emails',
                variant: 'destructive',
            });
        } finally {
            setLoading(false);
        }
    };

    return (
        <ScopingAILayout>
            <div className="w-full h-[calc(100vh-64px)] flex flex-col">
                <div className="mb-6 flex-shrink-0">
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                        <div>
                            <h1 className="text-3xl font-bold flex items-center gap-3">
                                <Mail className="w-8 h-8 text-blue-600" />
                                Email Management
                            </h1>
                            <p className="text-muted-foreground mt-2">
                                Manage your Gmail accounts and email communications with retailers
                            </p>
                        </div>
                        <div className="flex flex-col sm:flex-row gap-3 items-end sm:items-center">
                            {/* Global Filter */}
                            <div className="flex items-center gap-2 bg-white p-1 rounded-md border shadow-sm">
                                <Filter className="w-4 h-4 text-gray-500 ml-2" />
                                <select
                                    className="bg-transparent border-none text-sm focus:ring-0 cursor-pointer py-1.5 pr-8"
                                    value={selectedTenderId}
                                    onChange={(e) => setSelectedTenderId(e.target.value)}
                                >
                                    <option value="">All Tenders</option>
                                    {tenders.map(t => (
                                        <option key={t.id} value={t.id}>
                                            {t.opportunity_name || t.id.substring(0, 8)}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div className="flex gap-2">
                                <Button
                                    variant="outline"
                                    onClick={handlePollEmails}
                                    disabled={loading || accounts.length === 0}
                                >
                                    <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                                    Sync
                                </Button>
                                <Button
                                    onClick={() => setIsComposingEmail(true)}
                                    disabled={accounts.length === 0}
                                    className="bg-blue-600 hover:bg-blue-700"
                                >
                                    <Plus className="w-4 h-4 mr-2" />
                                    Compose
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Stats Cards */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6 flex-shrink-0">
                    <Card className="bg-gradient-to-br from-blue-50 to-white border-blue-100">
                        <CardContent className="pt-6 flex items-center justify-between">
                            <div>
                                <div className="text-sm text-gray-500 font-medium mb-1">Connected Accounts</div>
                                <div className="text-3xl font-bold text-blue-700">{stats.accounts}</div>
                            </div>
                            <div className="p-3 bg-blue-100 rounded-full text-blue-600">
                                <Settings className="w-6 h-6" />
                            </div>
                        </CardContent>
                    </Card>
                    <Card className="bg-gradient-to-br from-green-50 to-white border-green-100">
                        <CardContent className="pt-6 flex items-center justify-between">
                            <div>
                                <div className="text-sm text-gray-500 font-medium mb-1">Inbox Messages</div>
                                <div className="text-3xl font-bold text-green-700">{stats.inbox}</div>
                            </div>
                            <div className="p-3 bg-green-100 rounded-full text-green-600">
                                <Inbox className="w-6 h-6" />
                            </div>
                        </CardContent>
                    </Card>
                    <Card className="bg-gradient-to-br from-purple-50 to-white border-purple-100">
                        <CardContent className="pt-6 flex items-center justify-between">
                            <div>
                                <div className="text-sm text-gray-500 font-medium mb-1">Sent Messages</div>
                                <div className="text-3xl font-bold text-purple-700">{stats.sent}</div>
                            </div>
                            <div className="p-3 bg-purple-100 rounded-full text-purple-600">
                                <Send className="w-6 h-6" />
                            </div>
                        </CardContent>
                    </Card>
                </div>

                {/* Main Content Tabs */}
                <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col min-h-0">
                    <TabsList className="grid w-full grid-cols-5 mb-6 flex-shrink-0">
                        <TabsTrigger value="inbox" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white">
                            <Inbox className="w-4 h-4 mr-2" />
                            Inbox (Threads)
                        </TabsTrigger>
                        <TabsTrigger value="retailer-replies" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white">
                            <FileText className="w-4 h-4 mr-2" />
                            Retailer Replies
                        </TabsTrigger>
                        <TabsTrigger value="sent" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white">
                            <Send className="w-4 h-4 mr-2" />
                            Sent Messages
                        </TabsTrigger>
                        <TabsTrigger value="templates" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white">
                            <FileText className="w-4 h-4 mr-2" />
                            Templates
                        </TabsTrigger>
                        <TabsTrigger value="accounts" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white">
                            <Settings className="w-4 h-4 mr-2" />
                            Accounts
                        </TabsTrigger>
                    </TabsList>

                    <TabsContent value="inbox" className="flex-1 min-h-0 data-[state=active]:flex flex-col">
                        <Card className="flex-1 flex flex-col min-h-0 border-0 shadow-none">
                            <CardContent className="p-0 flex-1 flex flex-col min-h-0">
                                <RetailerCommunicationHub
                                    tenderId={selectedTenderId || undefined}
                                    lastSyncedAt={lastSyncedAt}
                                />
                            </CardContent>
                        </Card>
                    </TabsContent>

                    <TabsContent value="retailer-replies" className="flex-1 overflow-auto">
                        <RetailerResponseViewer
                            tenderId={selectedTenderId || undefined}
                            refreshTrigger={lastSyncedAt}
                        />
                    </TabsContent>

                    <TabsContent value="sent" className="flex-1 overflow-auto">
                        <Card className="h-full flex flex-col">
                            <CardHeader>
                                <CardTitle>Sent Emails</CardTitle>
                                <CardDescription>
                                    {selectedTenderId
                                        ? `Showing sent emails for tender: ${tenders.find(t => t.id === selectedTenderId)?.opportunity_name}`
                                        : "All emails sent to retailers"
                                    }
                                </CardDescription>
                            </CardHeader>
                            <CardContent className="flex-1 overflow-hidden">
                                <SentEmailList tenderId={selectedTenderId || undefined} />
                            </CardContent>
                        </Card>
                    </TabsContent>

                    <TabsContent value="templates" className="flex-1 overflow-auto">
                        <EmailTemplateManager />
                    </TabsContent>

                    <TabsContent value="accounts" className="flex-1 overflow-auto">
                        <Card>
                            <CardHeader>
                                <CardTitle>Email Account Settings</CardTitle>
                                <CardDescription>
                                    Connect and manage your Gmail accounts for retailer communications
                                </CardDescription>
                            </CardHeader>
                            <CardContent>
                                <EmailAccountManager />
                            </CardContent>
                        </Card>
                    </TabsContent>
                </Tabs>

                {/* Email Composition Dialog */}
                <EmailCompositionDialog
                    isOpen={isComposingEmail}
                    onClose={() => setIsComposingEmail(false)}
                    tenderId={selectedTenderId || ""} // Pre-select tender if filtered
                />
            </div>
        </ScopingAILayout>
    );
}
